import type { PhaseDefinition } from './types';

// ============================================================================
// Phase-Based Interview Structure (3 Phases)
// ============================================================================
// The agent is an interviewer, peer, and domain expert - not a checklist follower.

export const PHASES: PhaseDefinition[] = [
  {
    id: 'discovery',
    name: 'Kennismaking',
    description: 'Begrijp wie je gesprekspartner is en wat hen hier brengt',
    guidanceAreas: [
      {
        id: 'who',
        name: 'Wie',
        hints: ['Rol', 'Achtergrond', 'Expertise niveau', 'Hoe lang bij bedrijf'],
      },
      {
        id: 'where',
        name: 'Waar',
        hints: ['Bedrijf', 'Branche', 'Bedrijfsgrootte', 'Bedrijfscultuur'],
      },
      {
        id: 'why',
        name: 'Waarom',
        hints: ['Aanleiding gesprek', 'Eerste verwachtingen', 'Wat zoeken ze'],
      },
    ],
    transitionSignals: [
      'We weten wie ze zijn en wat hun rol is',
      'We begrijpen de bedrijfscontext',
      'We hebben een eerste indruk van wat ze zoeken',
      'De gesprekspartner voelt zich op gemak',
    ],
    extractionTargets: [
      'company_name',
      'company_description',
      'industry',
      'company_size',
      'role',
      'contact_role',
      'tenure',
      'culture',
      'contact_intent',
      'change_appetite',
    ],
  },
  {
    id: 'deep_dive',
    name: 'Verdieping',
    description: 'Rijk gesprek over uitdagingen, kansen en de weg vooruit',
    guidanceAreas: [
      {
        id: 'challenges',
        name: 'Uitdagingen',
        hints: ['Knelpunten', 'Impact', 'Concrete voorbeelden', 'Eerdere pogingen'],
      },
      {
        id: 'current',
        name: 'Het werk',
        hints: ['Taken die elke week terugkomen', 'Hoe het de laatste keer ging', 'Hoe vaak', 'Wie of wat het werk doet', 'Tools & systemen'],
      },
      {
        id: 'future',
        name: 'Toekomst',
        hints: ['Doelen', 'Visie', 'Succescriteria', 'KPIs'],
      },
      {
        id: 'context',
        name: 'Context',
        hints: ['Stakeholders', 'Wie wordt eigenaar', 'Toegang tot data', 'Besluitvorming', 'Timeline'],
      },
    ],
    transitionSignals: [
      'Concrete uitdagingen met impact zijn duidelijk',
      'Patronen en verbanden worden zichtbaar',
      'Gesprekspartner voelt zich gehoord en begrepen',
      'We hebben voldoende context voor zinvolle inzichten',
    ],
    extractionTargets: [
      'primary_challenges',
      'challenge_examples',
      'impact_metrics',
      'time_impact',
      'cost_impact',
      'failed_solutions',
      'affected_teams',
      'current_tools',
      'tech_stack',
      'manual_processes',
      'integration_status',
      'data_gaps',
      'automation_level',
      'vision_statement',
      'success_metrics',
      'kpis',
      'timeline_drivers',
      'priorities',
      'stakeholders',
      'decision_process',
      'champions',
      'potential_blockers',
      'budget_range',
      'budget_status',
      'ideal_timeline',
      'urgency_drivers',
    ],
  },
  {
    id: 'summary',
    name: 'Afsluiting',
    description: 'Deel inzichten, valideer begrip, beantwoord vragen',
    guidanceAreas: [
      {
        id: 'synthesize',
        name: 'Synthese',
        hints: ['Samenvatten', 'Patronen benoemen', 'Inzichten delen'],
      },
      {
        id: 'validate',
        name: 'Validatie',
        hints: ['Check begrip', 'Correcties vragen', 'Bevestiging'],
      },
      {
        id: 'questions',
        name: 'Vragen',
        hints: ['Ruimte voor hun vragen', 'Openstaande punten'],
      },
    ],
    transitionSignals: [
      'Begrip is gevalideerd door de gesprekspartner',
      'Geen grote correcties of aanvullingen nodig',
      'Ruimte gegeven voor vragen van de gesprekspartner',
      'Natuurlijk moment om af te ronden',
    ],
    extractionTargets: [
      'additional_context',
      'preferred_next_step',
      'follow_up_contacts',
      'documentation_available',
      'open_questions',
    ],
  },
];

// Helper to get all extraction targets across all phases
export function getAllExtractionTargets(): string[] {
  const targets = new Set<string>();
  for (const phase of PHASES) {
    for (const target of phase.extractionTargets) {
      targets.add(target);
    }
  }
  return Array.from(targets);
}

// Helper to get a phase by ID
export function getPhaseById(phaseId: string): PhaseDefinition | undefined {
  return PHASES.find((p) => p.id === phaseId);
}

// Helper to get next phase
export function getNextPhase(
  currentPhaseId: string
): PhaseDefinition | null {
  const currentIndex = PHASES.findIndex((p) => p.id === currentPhaseId);
  if (currentIndex === -1 || currentIndex >= PHASES.length - 1) {
    return null;
  }
  return PHASES[currentIndex + 1];
}
